import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Typography, Spin, message } from 'antd';
import {
  UserOutlined,
  FileTextOutlined,
  DollarOutlined,
  ApiOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { logApi } from '../../services/api';
import { SystemStats, LogStats } from '../../types';
import { usePermissions, usePermissionCheck } from '../../context/PermissionContext';
import { Line } from '@ant-design/plots';

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [logStats, setLogStats] = useState<LogStats | null>(null);
  const [dailyLogStats, setDailyLogStats] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  // 权限检查
  const { loading: permissionLoading } = usePermissions();
  const { canViewDashboard } = usePermissionCheck();

  useEffect(() => {
    if (!permissionLoading && canViewDashboard()) {
      fetchData();
    }
  }, [permissionLoading]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [systemResponse, logResponse, dailyLogResponse] = await Promise.all([
        logApi.getSystemStats(),
        logApi.getLogStats(),
        logApi.getDailyLogStats(),
      ]);
      setSystemStats(systemResponse.data);
      setLogStats(logResponse.data);
      setDailyLogStats(dailyLogResponse.data);
    } catch (error) {
      message.error('获取统计数据失败');
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 权限检查失败时的展示
  if (!canViewDashboard()) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>权限不足</Title>
          <p>您没有查看仪表盘的权限</p>
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // 操作类型统计表格数据
  const actionStatsData = logStats?.actions_stats ?
    Object.entries(logStats.actions_stats).map(([action, count], index) => ({
      key: index,
      action,
      count,
    })) : [];

  const actionStatsColumns = [
    {
      title: '操作类型',
      dataIndex: 'action',
      key: 'action',
    },
    {
      title: '次数',
      dataIndex: 'count',
      key: 'count',
      sorter: (a: any, b: any) => a.count - b.count,
    },
  ];

  // 每日调用趋势图表配置
  const dailyStatsData = Object.entries(dailyLogStats)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, count]) => ({
      date,
      count,
    }));

  const lineConfig = {
    data: dailyStatsData,
    xField: 'date',
    yField: 'count',
    smooth: true,
    point: {
      size: 5,
      shape: 'diamond',
    },
    label: {
      style: {
        fill: '#aaa',
      },
    },
  };

  return (
    <div>
      <Title level={2}>仪表盘</Title>

      {/* 用户统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="总用户数"
              value={systemStats?.total_users || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="活跃用户"
              value={systemStats?.active_users || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="新增用户"
              value={logStats?.new_users_24h || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* API调用和收入统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="API调用总数"
              value={systemStats?.total_api_calls || 0}
              prefix={<ApiOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="今日API调用"
              value={systemStats?.today_api_calls || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总收入"
              value={systemStats?.total_revenue || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="今日收入"
              value={systemStats?.today_revenue || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 日志统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card>
            <Statistic
              title="总日志数"
              value={logStats?.total_logs || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <Statistic
              title="今日日志"
              value={logStats?.today_logs || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 每日调用趋势图 */}
      <Card title="最近30天调用趋势" style={{ marginBottom: 24 }}>
        <Line {...lineConfig} />
      </Card>

      {/* 操作统计表格 */}
      <Card title="操作类型统计" style={{ marginBottom: 24 }}>
        <Table
          dataSource={actionStatsData}
          columns={actionStatsColumns}
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default Dashboard; 